import { ButtonBase, Collapse, styled, useTheme } from '@mui/material';
import { MainMenuItemProps } from '../../types';
import { FC, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Typography } from '../../components/Typography/Typography';
import { useAtom } from 'jotai';
import { sidebarNavSubmenuOpenState } from '../../state/UIState';
import { motion } from 'framer-motion';

export interface Props {
  item: MainMenuItemProps;
  /** Sidebar rail expanded (hover or pinned); mobile drawer treats as always expanded via `isSmallScreen`. */
  isExpanded?: boolean;
  isSmallScreen?: boolean;
}

const Wrapper = styled('div')<{ railExpanded: boolean; isSmallScreen: boolean }>`
  width: ${({ railExpanded, isSmallScreen }) =>
    isSmallScreen ? '100%' : railExpanded ? '264px' : '64px'};
  padding-left: 8px;
  padding-right: ${({ isSmallScreen }) => (isSmallScreen ? '8px' : '0px')};
`;

const MenuSubItemWrapper = styled('div')<{ isActive: boolean }>`
  cursor: pointer;
  height: 36px;
  display: flex;
  align-items: center;
  border-radius: 4px;
  padding-left: 8px;
  ${({ isActive, theme }) =>
    isActive ? `background-color: ${theme.colors.surfaceInteraction.selectedLight}` : ''};
`;

const SubMenuWrapper = styled('div')`
  padding: 8px 0 0 36px;
`;

export interface ButtonProps {
  isActive?: boolean;
  disableHover?: boolean;
  railExpanded?: boolean;
  isSmallScreen?: boolean;
}

const IconWrapper = styled('div')<ButtonProps>`
  min-height: 48px;
  width: ${({ railExpanded, isSmallScreen }) =>
    isSmallScreen ? '98%' : railExpanded ? '256px' : '48px'};
  border-radius: 4px;
  background-color: ${({ isActive, theme }) =>
    isActive ? theme.colors.surfaceInteraction.selectedLight : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: ${({ railExpanded, isSmallScreen }) =>
    !isSmallScreen && railExpanded ? 'space-between' : 'flex-start'};
  padding-left: 12px;
  padding-right: ${({ railExpanded, isSmallScreen }) =>
    !isSmallScreen && railExpanded ? '8px' : '0px'};
  cursor: pointer;
  gap: 12px;
  svg: {
    g {
      g {
        path {
          fill: ${({ isActive, theme }) =>
            isActive ? theme.colors.icon.accent : theme.colors.icon.default};
        }
      }
    }
  }
`;

const RowLeading = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
`;

const SvgWrapper = styled('div')``;

const ChevronIcon: FC<{ expanded: boolean; color: string }> = ({ expanded, color }) => (
  <svg
    width={20}
    height={20}
    viewBox='0 0 24 24'
    aria-hidden
    style={{
      flexShrink: 0,
      transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease',
    }}
  >
    <path
      fill={color}
      d='M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z'
    />
  </svg>
);

export const MenuItem: FC<Props> = ({ item, isExpanded = false, isSmallScreen = false }) => {
  const location = useLocation();
  const activeRoute = useMemo(() => location.pathname.split('/')[1], [location.pathname]);
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [submenuOpenById, setSubmenuOpenById] = useAtom(sidebarNavSubmenuOpenState);

  const railExpanded = isSmallScreen || isExpanded;

  const visibleChildren = useMemo(
    () => item.children.filter((subItem) => subItem.show),
    [item.children]
  );
  const hasSubmenu = visibleChildren.length > 0;

  const isChildActive = useMemo(
    () => visibleChildren.some((child) => child.id === activeRoute),
    [visibleChildren, activeRoute]
  );

  const isMainActive = hasSubmenu ? isChildActive : activeRoute === item.id;

  const storedOpen = submenuOpenById[item.id];
  const isSubmenuOpen = storedOpen !== undefined ? storedOpen : isChildActive;

  const toggleSubmenu = () => {
    const current = submenuOpenById[item.id] ?? isChildActive;
    setSubmenuOpenById((prev) => ({ ...prev, [item.id]: !current }));
  };

  const onNavigate = (route: string) => {
    navigate(route);
  };

  const onHeaderClick = () => {
    if (!railExpanded) {
      onNavigate(item.route);
      return;
    }
    if (hasSubmenu) {
      toggleSubmenu();
      return;
    }
    onNavigate(item.route);
  };

  if (!item.show) return null;

  const chevronColor = isMainActive ? colors.icon.accent : colors.icon.default;

  return (
    <Wrapper railExpanded={railExpanded} isSmallScreen={isSmallScreen}>
      <ButtonBase
        TouchRippleProps={{ color: 'white' }}
        style={{
          borderRadius: '4px',
          width: isSmallScreen ? '100%' : railExpanded ? '256px' : '48px',
        }}
        onClick={onHeaderClick}
        aria-expanded={hasSubmenu && railExpanded ? isSubmenuOpen : undefined}
      >
        <IconWrapper
          isActive={isMainActive}
          railExpanded={railExpanded}
          isSmallScreen={isSmallScreen}
        >
          <RowLeading>
            <SvgWrapper
              sx={{
                svg: {
                  path: { fill: isMainActive ? colors.icon.accent : colors.icon.default },
                },
                marginBottom: '-2px',
                flexShrink: 0,
              }}
            >
              {item.icon}
            </SvgWrapper>
            {railExpanded && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
              >
                <Typography
                  variant='subtitle2'
                  color={colors.text.main}
                  style={{ marginBottom: '-2px' }}
                >
                  {item.title}
                </Typography>
              </motion.div>
            )}
          </RowLeading>
          {hasSubmenu && railExpanded && (
            <ChevronIcon expanded={isSubmenuOpen} color={chevronColor} />
          )}
        </IconWrapper>
      </ButtonBase>

      {hasSubmenu && railExpanded && (
        <Collapse in={isSubmenuOpen} timeout='auto'>
          <SubMenuWrapper>
            {visibleChildren.map((subItem) => (
              <MenuSubItemWrapper
                key={subItem.route}
                isActive={activeRoute === subItem.id}
                onClick={() => onNavigate(subItem.route)}
              >
                <Typography variant='subtitle1' color={colors.text.main}>
                  {subItem.title}
                </Typography>
              </MenuSubItemWrapper>
            ))}
          </SubMenuWrapper>
        </Collapse>
      )}
    </Wrapper>
  );
};
